import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { loginUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TrendUp, ChartLineUp, Wallet, ShieldCheck, GoogleLogo, Warning } from "@phosphor-icons/react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  // The Worker redirects back with ?auth_error=... when Google declines or the
  // CSRF state fails to validate.
  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get("auth_error");
    if (reason) {
      setAuthError(reason.replace(/_/g, " "));
      window.history.replaceState({}, document.title, "/");
    }
  }, []);

  // Sign-in is started on the API, not in the browser: the authorize URL is
  // built server-side so the client id and the CSRF state never originate here.
  const handleLogin = () => {
    window.location.href = loginUrl();
  };

  return (
    <div className="min-h-dvh bg-background text-foreground grid lg:grid-cols-2">
      {/* Left: brand + copy */}
      <div className="relative flex flex-col justify-between p-8 lg:p-14 border-r border-border overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-8 h-8 bg-income flex items-center justify-center rounded-sm">
            <TrendUp size={18} weight="bold" className="text-black" />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Fin<span className="text-income">Flo</span></span>
        </div>

        <div className="relative z-10 max-w-md">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Personal finance intelligence</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Know your money.
            <br />
            <span className="text-income">Forecast</span> your future.
          </h1>
          <p className="text-muted-foreground mt-6 text-base leading-relaxed">
            Track income and expenses, understand your cash flow, and project your balance
            over the next 3, 6, 12 and 24 months — with deterministic, transparent math.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4 max-w-md">
          {[
            { icon: Wallet, label: "Cash flow" },
            { icon: ChartLineUp, label: "Forecasting" },
            { icon: ShieldCheck, label: "Scenarios" },
          ].map((f, i) => (
            <div key={i} className="border border-border rounded-md p-3 bg-card">
              <f.icon size={20} className="text-income mb-2" />
              <p className="text-xs text-muted-foreground">{f.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right: auth */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h2 className="font-heading font-bold text-2xl tracking-tight mb-1">Welcome back</h2>
          <p className="text-muted-foreground text-sm mb-8">Sign in to open your financial dashboard.</p>
          {authError && (
            <div
              role="alert"
              data-testid="login-error"
              className="mb-5 flex items-start gap-2 rounded-md border border-expense/40 bg-expense/10 p-3"
            >
              <Warning size={16} className="text-expense mt-0.5 shrink-0" />
              <p className="text-xs text-expense leading-relaxed">
                Sign-in failed: {authError}. Please try again.
              </p>
            </div>
          )}
          <Button
            data-testid="google-login-button"
            onClick={handleLogin}
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-md gap-2"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </Button>
          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
            Your account is seeded with realistic demo data on first sign-in, so you can explore
            the dashboard and forecasting immediately.
          </p>
        </div>
      </div>
    </div>
  );
}
